import {
  CsrfToken,
  DisableAuthenticationRequest,
  EnableAuthenticationRequest,
  EnableAuthenticationResponse,
  LoginResponse,
  MFAResponse,
  SharedKeyResponse,
  UserInfoModel
} from '../models/api';
import api, { getData, postData } from './api';
import { getItem, getUseLocalStorage, setItem } from './local.service';
import { BasicResponse, ErrorResponse, ResponseData } from './models';

// TODO: Use API methods not axios api directly
export const login = async (
  emailAddress: string,
  password: string,
  rememberMe: boolean
): Promise<ResponseData<LoginResponse>> => {
  try {
    await api.post(
      `login?useSessionCookies=${!rememberMe}`,
      {
        email: emailAddress,
        password
      },
      {
        headers: {
          'Skip-Auth-Interceptor': 'true'
        }
      }
    );

    setItem('useLocalStorage', rememberMe.toString(), true);

    const info = await api.get('api/user/info');
    setItem('info', JSON.stringify(info.data.responseData), rememberMe);

    return { success: true, errors: [] };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (
      error?.response?.status === 401 &&
      error?.response?.data?.responseData?.detail === 'RequiresTwoFactor'
    ) {
      return { success: true, errors: [], data: { requiresTwoFactor: true } };
    }

    if (
      error?.response?.status === 400 &&
      error?.response?.data === 'CSRF token validation failed.'
    ) {
      // Possibly already logged in in another tab, CSRF mismatch
      try {
        const session = await api.get('api/auth/me');
        if (session.data?.responseData) {
          return { success: true, errors: [], data: { alreadyLoggedIn: true } };
        }
      } catch {
        // Not authenticated, real CSRF issue
      }
    }

    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['There was a problem logging in.  Please try again.']
    };
  }
};
export const loginMFA = async (
  code: string,
  rememberMachine: boolean,
  rememberMe: boolean,
  useRecoveryCode: boolean
): Promise<ResponseData<LoginResponse>> => {
  try {
    await api.post(
      `loginMFA`,
      {
        code,
        rememberMachine,
        rememberMe,
        useRecoveryCode
      },
      {
        headers: {
          'Skip-Auth-Interceptor': 'true'
        }
      }
    );
    setItem('useLocalStorage', rememberMe.toString(), true);

    const info = await api.get('api/user/info');
    setItem('info', JSON.stringify(info.data.responseData), rememberMe);
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export const loginSSO = async (
  idToken: string,
  rememberMe: boolean
): Promise<ResponseData<RegisterResponse>> => {
  try {
    await api.post('api/auth/google', { idToken });
    //const { accessToken, refreshToken } = response.data;

    // Store the tokens in localStorage or secure cookie for later use
    setItem('useLocalStorage', rememberMe.toString(), true);
    //setItem('accessToken', accessToken, rememberMe);
    //setItem('refreshToken', refreshToken, rememberMe);

    const info = await api.get('api/user/info');
    setItem('info', JSON.stringify(info.data.responseData), rememberMe);
    //setItem('user', JSON.stringify(response.data), rememberMe);
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export const forgotPassword = async (
  emailAddress: string,
  challengeResponseCode: string
) => {
  try {
    await api.post(
      'api/Auth/forgotPassword',
      {
        email: emailAddress
      },
      {
        headers: {
          'Skip-Auth-Interceptor': 'true',
          'Challenge-Response-Code': challengeResponseCode
        }
      }
    );
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: ['There was a problem, please try again']
    };
  }
};

export const resetPassword = async (
  emailAddress: string,
  code: string,
  password: string
) => {
  try {
    await api.post('api/Auth/resetPassword', {
      email: emailAddress,
      resetCode: code,
      newPassword: password
    });
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export const changePassword = async (password: string, newPassword: string) => {
  try {
    await api.post('api/Auth/changePassword', { password, newPassword });
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export interface RegisterResponse {
  requireConfirmedAccount?: boolean;
}

export const register = async (
  firstName: string,
  lastName: string,
  emailAddress: string,
  password: string,
  confirmPassword: string,
  challengeResponseCode: string,
  inviteCode?: string | null
): Promise<ResponseData<RegisterResponse>> => {
  try {
    const response = await api.post(
      'api/auth/register',
      {
        firstName,
        lastName,
        email: emailAddress,
        password,
        confirmPassword,
        inviteCode
      },
      {
        headers: {
          'Skip-Auth-Interceptor': 'true',
          'Challenge-Response-Code': challengeResponseCode
        }
      }
    );

    // Don't auto-login (because of challengeResponseCode), redirect to login page
    //if (!response.data.responseData.requireConfirmedAccount) {
    //  await login(emailAddress, password, false, challengeResponseCode);
    //}

    return {
      success: true,
      data: {
        requireConfirmedAccount:
          response.data.responseData.requireConfirmedAccount
      },
      errors: []
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export const update = async (
  firstName: string,
  lastName: string
): Promise<ResponseData<BasicResponse>> => {
  try {
    await api.post('api/user/update', { firstName, lastName });
    const info = await api.get('api/user/info');
    setItem(
      'info',
      JSON.stringify(info.data.responseData),
      getUseLocalStorage()
    );
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export const refresh = async (): Promise<ResponseData<BasicResponse>> => {
  try {
    const res = await getSelf();
    if (res.success && res.data) {
      const data: UserInfoModel = res.data;
      setItem('info', JSON.stringify(data), getUseLocalStorage());
      return { success: true, errors: [] };
    }
    return { success: false, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      errors: error.response?.data?.errors?.map(
        (p: ErrorResponse) => p.message
      ) ?? ['Unexpected error']
    };
  }
};

export const confirmEmail = async (
  userId: string,
  code: string
): Promise<ResponseData<BasicResponse>> => {
  try {
    await api.post(`api/auth/confirmEmail?userId=${userId}&code=${code}`, {});
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return { success: false, errors: ['Unexpected error'] };
  }
};

export const confirmInvite = async (
  userId: string,
  code: string,
  firstName: string,
  lastName: string,
  password: string,
  confirmPassword: string
): Promise<ResponseData<BasicResponse>> => {
  try {
    await api.post(`api/auth/confirmInvite?userId=${userId}&code=${code}`, {
      firstName,
      lastName,
      password,
      confirmPassword
    });
    return { success: true, errors: [] };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return { success: false, errors: ['Unexpected error'] };
  }
};

export const confirmInviteStatusRequest = async (
  userId: string,
  code: string
): Promise<ResponseData<BasicResponse>> =>
  await getData<BasicResponse>(`api/auth/confirmInvite/${userId}/${code}`);

export const deleteSelf = async (
  password: string
): Promise<ResponseData<BasicResponse>> =>
  await postData<BasicResponse>('api/user/removeSelf', { password });

export const requestChangeEmail = async (
  newEmailAddress: string,
  password: string
): Promise<ResponseData<BasicResponse>> =>
  postData<BasicResponse>('api/Auth/requestChangeEmail', {
    newEmailAddress,
    password
  });

export const confirmChangeEmail = async (
  newEmailAddress: string,
  token: string
): Promise<ResponseData<BasicResponse>> =>
  postData<BasicResponse>('api/Auth/confirmChangeEmail', {
    newEmailAddress,
    token
  });

export const logout = async () => {
  localStorage.removeItem('useLocalStorage');
  localStorage.removeItem('info');
  sessionStorage.removeItem('info');
  await api.get('logout');
  //localStorage.removeItem('user');
  //localStorage.removeItem('accessToken');
  //localStorage.removeItem('refreshToken');
  //sessionStorage.removeItem('user');
  //sessionStorage.removeItem('accessToken');
  //sessionStorage.removeItem('refreshToken');
};
/*
export const getCurrentUser = () => {
    const user = getItem('user');
    if (user !== null) {
        return JSON.parse(user);
    }
    return null;
}*/

// TODO: Consider removing this if it's no longer needed
export const getUserInfo = (): UserInfoModel | null => {
  const user = getItem('info');
  if (user !== null) {
    return JSON.parse(user) as UserInfoModel;
  }
  return null;
};

export const getSharedKey = async (): Promise<
  ResponseData<SharedKeyResponse>
> => await getData<SharedKeyResponse>('api/auth/sharedKey');

export const getSelf = async (): Promise<ResponseData<UserInfoModel>> =>
  await getData<UserInfoModel>('api/auth/me', true);

export const enableAuthentication = async (
  request: EnableAuthenticationRequest
): Promise<ResponseData<EnableAuthenticationResponse>> =>
  await postData<EnableAuthenticationResponse>(
    'api/auth/enableAuthentication',
    request
  );

export const getMFA = async (): Promise<ResponseData<MFAResponse>> =>
  await getData<MFAResponse>('api/auth/MFA');

export const disableAuthentication = async (
  request: DisableAuthenticationRequest
): Promise<ResponseData<BasicResponse>> =>
  await postData<BasicResponse>('api/auth/disableAuthentication', request);

export const forgetMFAClient = async (): Promise<ResponseData<BasicResponse>> =>
  await postData<BasicResponse>('api/auth/forgetMFABrowser', {});

export const getCsrfToken = async (): Promise<ResponseData<CsrfToken>> =>
  await getData<CsrfToken>('/api/csrf/get-csrf-token');
